// ISOLATED PROPOSAL - NOT INTEGRATED. "Team & access" section for MerchantSettings (/dashboard/settings).
// Style mirrors MerchantSettings.js cards (#0B1120 bg, #0E1426 cards, #22304A borders).
// Data contract = make_members_routes (merchant_members.py): GET /api/businesses/{id}/members,
// POST /api/businesses/invite, PUT /api/businesses/{id}/members/{email}/role,
// DELETE /api/businesses/access/{id}/{email}. Bearer session token only (post-Packet-A path).
import React, { useState, useEffect, useCallback } from "react";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Admin",
  staff: "Staff",
  read_only: "Read-only",
};
const ROLE_DESCRIPTIONS = {
  owner: "Full control, including billing and API keys",
  admin: "Profile, store operations and settings; manages Staff and Read-only members",
  staff: "Store operations; cannot manage users or settings",
  read_only: "View dashboards and reports only",
};
const ROLE_BADGE = {
  owner: "bg-[#8CF78E]/10 border-[#8CF78E]/30 text-[#8CF78E]",
  admin: "bg-[#6EA8FE]/10 border-[#6EA8FE]/30 text-[#6EA8FE]",
  staff: "bg-[#FFC46B]/10 border-[#FFC46B]/30 text-[#FFC46B]",
  read_only: "bg-[#9CB0C7]/10 border-[#9CB0C7]/30 text-[#9CB0C7]",
};
const ASSIGNABLE = ["admin", "staff", "read_only"]; // owner is never assigned (approved matrix)



// Invitee surface: pending invitations addressed to the signed-in user, shown on
// their next sign-in. Accept/decline happen in-product; nothing is emailed automatically.
export function PendingInvitesBanner({ fetchWithAuth, onChanged }) {
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`/api/businesses/invitations/mine`);
      setInvites(data?.invitations || []);
    } catch (e) {
      setInvites([]);
    } finally { setLoaded(true); }
  }, [fetchWithAuth]);
  useEffect(() => { load(); }, [load]);

  const respond = async (merchantId, action) => {
    setBusy(true);
    try {
      await fetchWithAuth(`/api/businesses/${action}/${merchantId}`, { method: "POST" });
      await load();
      if (onChanged) onChanged();
    } catch (e) { /* keep banner state; surface on next load */ }
    finally { setBusy(false); }
  };

  if (!loaded || invites.length === 0) return null;
  return (
    <div className="rounded-lg border border-[#FFC46B]/30 bg-[#FFC46B]/5 p-4 mb-6">
      <p className="text-sm font-semibold text-white mb-1">You have a pending invitation</p>
      {invites.map((inv) => (
        <div key={inv.merchant_id} className="flex items-center justify-between mt-3">
          <div>
            <p className="text-sm text-white">{inv.business_name || inv.merchant_id}</p>
            <p className="text-xs text-[#9CB0C7]">
              Invited as {ROLE_LABELS[inv.role] || inv.role}
              {inv.invited_by ? ` by ${inv.invited_by}` : ""} - accept to get access on this account.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => respond(inv.merchant_id, "accept")}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-[#3EF1C7] text-[#0B1120] text-xs font-semibold hover:bg-[#62f5d2] disabled:opacity-40"
            >
              Accept
            </button>
            <button
              onClick={() => respond(inv.merchant_id, "decline")}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg border border-[#FF7A90]/30 text-[#FF7A90] text-xs hover:bg-[#FF7A90]/10 disabled:opacity-40"
            >
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TeamAccessSection({ merchantId, currentRole: currentRoleProp, fetchWithAuth }) {
  const [members, setMembers] = useState([]);
  const [serverRole, setServerRole] = useState(null);
  const currentRole = serverRole || currentRoleProp;
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("staff");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const canManage = currentRole === "owner" || currentRole === "admin";

  const load = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`/api/businesses/${merchantId}/members`);
      setMembers(data?.members || []);
      if (data?.caller_role) setServerRole(data.caller_role);
    } catch (e) {
      setError("Could not load team members");
    } finally {
      setLoading(false);
    }
  }, [merchantId, fetchWithAuth]);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    setBusy(true); setError(null);
    try {
      await fetchWithAuth(`/api/businesses/invite`, {
        method: "POST",
        body: JSON.stringify({ merchant_id: merchantId, email: inviteEmail.trim(), role: inviteRole }),
      });
      setInviteEmail(""); setInviteRole("staff");
      await load();
    } catch (e) { setError("Could not create the invitation - check the email and role"); }
    finally { setBusy(false); }
  };

  const changeRole = async (email, role) => {
    setBusy(true); setError(null);
    try {
      await fetchWithAuth(`/api/businesses/${merchantId}/members/${encodeURIComponent(email)}/role`, {
        method: "PUT", body: JSON.stringify({ role }),
      });
      await load();
    } catch (e) { setError("Role change failed"); }
    finally { setBusy(false); }
  };

  const revoke = async (email) => {
    setBusy(true); setError(null);
    try {
      await fetchWithAuth(`/api/businesses/access/${merchantId}/${encodeURIComponent(email)}`, { method: "DELETE" });
      await load();
    } catch (e) { setError("Remove failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)] mb-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-1">Team &amp; access</h3>
        <p className="text-sm text-[#9CB0C7]">
          Who can sign in to this business and what they can do. New members appear as pending here
          and accept the invitation on their next sign-in - nothing is emailed automatically yet. Notification recipients are managed
          separately under Team &amp; Alerts.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-[#9CB0C7]">Loading team...</p>
      ) : (
        <div className="space-y-3 mb-6">
          {members.map((m) => (
            <div key={m.email} className="flex items-center justify-between p-4 bg-[#111A2E] rounded-lg">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm font-medium text-white">{m.email}</p>
                  <p className="text-xs text-[#9CB0C7]">{ROLE_DESCRIPTIONS[m.role] || m.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {m.status === "pending" && (
                  <span className="text-xs px-2 py-1 rounded border border-[#FFC46B]/30 text-[#FFC46B]">Invite pending</span>
                )}
                <span className={`text-xs px-2 py-1 rounded border ${ROLE_BADGE[m.role] || ROLE_BADGE.read_only}`}>
                  {ROLE_LABELS[m.role] || m.role}
                </span>
                {canManage && m.role !== "owner" && (
                  <>
                    <select
                      value={m.role}
                      disabled={busy || (currentRole === "admin" && m.role === "admin")}
                      onChange={(e) => changeRole(m.email, e.target.value)}
                      className="text-xs bg-[#0B1120] border border-[#22304A] text-white rounded px-2 py-1"
                    >
                      {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                    <button
                      onClick={() => revoke(m.email)}
                      disabled={busy || (currentRole === "admin" && m.role === "admin")}
                      className="text-xs px-2 py-1 rounded border border-[#FF7A90]/30 text-[#FF7A90] hover:bg-[#FF7A90]/10 disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className="flex items-center gap-3 p-4 bg-[#111A2E] rounded-lg">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="teammate@company.com"
            className="flex-1 bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-sm text-white placeholder-[#9CB0C7]"
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="bg-[#0B1120] border border-[#22304A] text-white rounded-lg px-3 py-2 text-sm"
          >
            {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <button
            onClick={invite}
            disabled={busy || !inviteEmail.trim()}
            className="px-4 py-2 rounded-lg bg-[#3EF1C7] text-[#0B1120] text-sm font-semibold hover:bg-[#62f5d2] disabled:opacity-40"
          >
            Create invitation
          </button>
        </div>
      )}
      {error && <p className="text-sm text-[#FF7A90] mt-3">{error}</p>}
    </div>
  );
}
